<script>
import { debounce } from 'lodash';

import mapValues from 'lodash/mapValues';

import Intersect from 'vue-intersect';

import TableFilter from '@/common/TableFilter.vue';
const addFilterLabelHtml = `<svg xmlns="http://www.w3.org/2000/svg" fill="#a7aed9" width="14" height="14" viewBox="0 0 16 12"><g transform="translate(0 0)"><rect width="16" height="2" rx="1" /><rect width="11" height="2" rx="1" transform="translate(3 5)" /><rect width="5" height="2" rx="1" transform="translate(6 10)" /></g></svg>&nbsp;<span>Add filter</span>`;

const vm = {
  name: 'DataTable',
  components: {
    Intersect,
    TableFilter
  },
  props: {
    getterFunction: {
      type: Function
    },
    initialSort: {
      type: Object,
      default: {}
    },
    columns: {
      type: Array,
      default: () => []
    },
    sortable: {
      type: Object,
      default: () => ({})
    },
    itemRoute: [String, Function],
    itemRouteParams: {
      type: Function,
      default: null
    },
    showIndicator: {
      type: Boolean,
      default: true
    },
    keyName: {
      type: String,
      default: 'id'
    },
    selfRoute: String,
    selfRouteParams: {
      type: Object,
      default: () => ({})
    },
    filters: {
      type: [],
      default: () => ({})
    },
    filtersOpen: {
      type: Boolean,
      default: false
    },
    actions: {
      type: Array,
      default: () => []
    },
    empty: {
      type: Object,
      default: () => ({
        message: 'No items found',
        icon: 'cube'
      })
    }
  },
  data() {
    return {
      items: [],
      isLoading: false,
      isLoadingMore: false,
      totalItems: 0,
      errorLoading: false,
      showFilters: false,
      activeFilters: [],
      addFilterLabelHtml,
      params: {
        filter: {},
        order: this.initialSort.field,
        desc: this.initialSort.type === 'desc',
        offset: 0,
        limit: this.limit || 30
      },
      bulkActions: {
        show: false,
        selected: {},
        lastSelected: null,
        selectAll: false
      },
      form: {},
      sortBy: '',
      initialized: false
    };
  },
  computed: {
    hasSearch() {
      return Object.prototype.hasOwnProperty.call(this.filters, 'search');
    },
    hasFilters() {
      return Object.keys(this.filters).filter((key) => key != 'search').length;
    },
    selectedRows() {
      let selected = [];
      Object.entries(this.bulkActions.selected).forEach((entry) => {
        if (entry[1]) {
          selected.push(entry[0]);
        }
      });
      return selected;
    }
  },
  watch: {
    'bulkActions.selectAll'(selectAll) {
      this.items.forEach((item) => {
        this.bulkActions.selected[item[this.keyName]] = selectAll;
      });
    },

    sortBy: {
      handler() {
        this.reloadData();
      }
    }
  },
  mounted() {
    // Initialize activeFilters with any filters that have activeByDefault set to true
    this.filters.forEach((filter) => {
      if (filter.activeByDefault) {
        this.addFilter(filter);

        if (filter.type === 'string') {
          this.setFilter(filter.field, '');
        }
      }
    });
    this.loadItems();
  },
  methods: {
    async loadItems() {
      if (this.isLoading) {
        return;
      }

      try {
        this.isLoading = true;
        const { data, error } = await this.getterFunction(this.params);

        if (error) {
          throw new Error(data);
        }
        this.errorLoading = false;
        this.totalItems = data.count || 0;
        this.items = data.rows || [];

        this.isLoading = false;
      } catch (err) {
        this.totalItems = 0;
        this.items = [];
        this.isLoading = false;
        this.errorMessage = err.message;
        this.errorLoading = true;
      }
    },

    async loadMoreItems() {
      if (this.isLoadingMore || this.items.length >= this.totalItems) {
        return;
      }
      try {
        this.isLoadingMore = true;
        const params = { ...this.params, offset: this.items.length, limit: 30 };
        const { data, error } = await this.getterFunction(params);
        if (error) {
          throw data;
        }
        this.items.push(...data.rows);
      } finally {
        this.isLoadingMore = false;
      }
    },

    setSort(column) {
      const sortableColumnName = this.sortable[column];
      if (sortableColumnName) this.sortBy = this.sortBy == sortableColumnName ? `-${sortableColumnName}` : sortableColumnName;
    },

    getItemRoute(item) {
      if (!this.itemRoute) return;
      if (typeof this.itemRoute === 'function') {
        return this.itemRoute(item);
      }
      let params = this.itemRouteParams ? this.itemRouteParams(item) : item[this.keyName];

      return this.$router.resolve({
        name: this.itemRoute,
        params: { id: params }
      }).route.path;
    },
    async dispatchAction(action) {
      if (!this.selectedRows.length) return;
      await action.action(this.selectedRows);
    },
    handleRowCheck(e, key, i) {
      // UNselecting
      if (!e.target.checked) {
        this.bulkActions.lastSelected = null;
        return;
      }

      if (e.shiftKey && this.bulkActions.lastSelected !== null) {
        const start = i;
        const end = this.bulkActions.lastSelected;
        Object.values(this.items).forEach((item, j) => {
          if (j > Math.min(start, end) && j < Math.max(end, start)) {
            this.bulkActions.selected[item[this.keyName]] = true;
          }
        });
      }

      this.bulkActions.lastSelected = i;
    },
    reset() {
      this.form = mapValues(this.form, () => null);
    },
    isFilterSelected(filter) {
      return this.activeFilters.some((activeFilter) => activeFilter === filter);
    },
    addFilter(filter) {
      const isAlreadyActive = this.activeFilters.some((activeFilter) => activeFilter === filter);
      if (isAlreadyActive) return;

      this.activeFilters.push(filter);
    },
    removeFilter(filter) {
      delete this.params.filter[filter.field];
      this.activeFilters = this.activeFilters.filter((item) => item !== filter);
      this.loadItemsDebounced();
    },

    setFilter(field, value) {
      if (value) {
        this.params.filter[field] = value;
        this.loadItemsDebounced();
      } else if (this.params.filter[field] !== undefined) {
        delete this.params.filter[field];
        this.loadItemsDebounced();
      }
    },
    reloadPage() {
      location.reload();
    }
  }
};
vm.methods.loadItemsDebounced = debounce(vm.methods.loadItems, 500);

export default vm;
</script>

<template>
  <div>
    <div class="overflow-x-auto overflow-y-hidden rounded bg-white">
      <div class="flex justify-between mb-4">
        <!-- <table-filters :filters="filters" /> -->
        <div v-if="filters.length && filtersOpen" class="tool-btn-group flex items-stretch overflow-x-auto divide-s">
          <slot name="filters" :form="form" />
        </div>

        <div v-else-if="hasFilters" v-popover:filters class="color-block selector flex items-center gap-2">
          <app-dropdown @toggle="showFilters = $event" :open="showFilters" class="add-filter-button">
            <template #label>
              <app-icon type="svg" name="filter" class="text-purple-light-3 fill-current" />
              <span class="text-sm">Add filter</span>
            </template>
            <div
              v-for="filter in filters"
              @click="
                addFilter(filter);
                showFilters = false;
              "
              :key="filter.label"
              :class="{ selected: isFilterSelected(filter) }"
              class="option"
            >
              {{ filter.label }}
            </div>
          </app-dropdown>
          <table-filter
            v-for="filter in activeFilters"
            @remove="removeFilter(filter)"
            @setFilter="setFilter(filter.field, $event)"
            :key="filter.label"
            :filter-data="filter"
          />
          <!-- <button  :class="{ active: showFilters }" class="tool-btn" @click="showFilters = !showFilters"> -->
          <!-- <button class="flex gap-2 items-center border border-purple-light-3 rounded-lg px-4 py-2">
            <app-icon type="svg" name="filter" class="text-purple-light-3 fill-current" />
            <span class="text-purple-light-3">Add filter</span>
          </button> -->
          <!-- </button> -->
        </div>
        <slot name="actions" />
        <!-- <popover name="filters">

          <div
            v-for="filter in filters"
            @click="addFilter(filter)"
            :key="filter.label"
            class="py-o px-4 font-normal font-sm text-purple-light-3 cursor-pointer"
          >
            {{ filter.label }}
          </div>
        </popover> -->
      </div>

      <table class="w-full whitespace-nowrap" cellspacing="0">
        <tr class="select-none bg-gray-50 text-xs text-gray-600 text-start">
          <th
            v-for="col in columns"
            :key="col.field"
            class="text-start py-2 px-4 text-base align-middle whitespace-nowrap text-purple-light bg-gray-light-2 select-none first-of-type:rounded-s-lg last-of-type:rounded-e-lg"
            :class="sortBy.match(col) ? 'bg-blue-100 hover:bg-blue-200' : 'hover:bg-blue-50'"
            @click="setSort(col)"
          >
            {{ col.label }}
          </th>
          <!-- <th class="px-6 py-3" colspan="2"></th> -->
        </tr>
        <table-row v-for="(item, rowIndex) in items" :key="item.id" :item="item" :href="getItemRoute(item)">
          <transition name="slideIn">
            <table-column v-if="bulkActions.show" :link="false" class="w-8">
              <div class="relative z-20">
                <input v-model="bulkActions.selected[item[keyName]]" type="checkbox" @click.stop @click="handleRowCheck($event, item[keyName], rowIndex)" />
              </div>
            </table-column>
          </transition>
          <table-column v-for="col in columns" :key="col.field" class="relative" :is-last="rowIndex === items.length - 1">
            <slot :name="col.field" :item="item">
              {{ item[col.field]?.length > 20 ? `${item[col.field].slice(0, 20)}...` : item[col.field] }}
            </slot>
            <!-- <router-link :to="getItemRoute(item)" class="absolute inset-0" /> -->
          </table-column>
          <!-- <table-column align="justify-end">
            <slot name="meta" :item="item" />
          </table-column> -->
        </table-row>
        <tr v-if="items.length === 0">
          <td :colspan="columns.length + 2" class="py-12">
            <div v-if="isLoading" class="text-primary flex flex-col items-center gap-4">
              <app-loader color="var(--primary-color)" class="loader" />
              <span>Loading...</span>
            </div>

            <div v-else-if="errorLoading" class="error m-9 flex flex-col justify-center items-center">
              <app-icon type="svg" name="alert" />
              <h1 class="mt-6 text-lg text-primary">Oops... We ran into an error</h1>
              <p class="text-xs color-purple-light-3">
                {{ errorMessage }}
              </p>

              <h3 class="my-12 mx-8 text-base font-medium text-purple-light">
                Please try again or
                <span @click="reloadPage" class="underline cursor-pointer transition-colors hover:text-primary">refresh the page</span>
              </h3>
            </div>

            <div v-else class="flex flex-col items-center justify-center p-12">
              <img src="@/assets/no-results.png" />
              <h1 class="text-primary mt-6 text-2xl font-extrabold">No results found</h1>

              <p class="text-purple-light-3 text-base">It seems we can’t find any results based on your search.</p>
            </div>
          </td>
        </tr>
      </table>
      <intersect @enter="loadMoreItems">
        <div class="mt-4 space-y-4">
          <app-loader v-if="isLoadingMore" color="var(--primary-color)" class="loader" />
          <div class="text-purple-light-3 text-xs text-center">
            Showing
            <span class="font-bold">{{ items.length | number }}</span> of <span class="font-bold">{{ totalItems | number }}</span> rows
          </div>
        </div>
      </intersect>
    </div>
  </div>
</template>

<style lang="scss">
table {
  border-spacing: 0;
  border-collapse: collapse;
}
td {
  padding: 0px;
}
</style>
